import {
  createCommendaOSEntity,
  deleteCommendaOSEntity,
} from '@commenda/api/commenda-os-dsl';
import { CorporationType } from '@prisma/client';
import type {
  MembershipComputation,
  PartnershipComputation,
  SharesComputation,
} from './compute';
import type { OwnershipFieldValue } from './types';

/**
 * Raised when a write in the pass fails, carrying the value as it stands.
 *
 * The corporations minted before the failure are already in the database, so the entries that
 * name them have to reach the saved response even though the submit did not finish. Without
 * that, the retry mints a second corporation for the same owner, and the shareholding it then
 * asks for lands on top of what the first pass already wrote.
 */
export class OwnershipWriteError extends Error {
  constructor(
    readonly cause: unknown,
    readonly valueToPersist: OwnershipFieldValue
  ) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'OwnershipWriteError';
  }
}

/** The value with each entry that minted a corporation now naming it. */
function withMintedIds(
  value: OwnershipFieldValue,
  minted: ReadonlyMap<string, number>
): OwnershipFieldValue {
  if (minted.size === 0) return value;
  const entries = { ...value.entries };
  minted.forEach((corporationId, entryId) => {
    const entry = entries[entryId];
    if (!entry || entry.owner.kind !== 'entity') return;
    entries[entryId] = { ...entry, owner: { ...entry.owner, corporationId } };
  });
  return { ...value, entries };
}

// The stock class minted for every owner in a reconcile. The same object is sent
// with each owner so the backend mints exactly one "Common" class (it
// find-or-creates by name) and reuses it. Membership records the unit mode via
// `hasIssuedUnits`; the unit count lives in `authorizedShares` either way (units
// are shares in the new model).
interface NewStockClassPayload {
  name: string;
  type: string;
  authorizedShares: number;
  issuedShares: number;
  hasIssuedUnits?: boolean;
  managerManaged?: boolean;
  pricePerShare?: { amount: number; currency: string };
}

// Shared submit-time reconcile: write each staged owner as a `shareholding` via
// dsl/execute, all carrying the same `newStockClass`. Writes are sequential so
// the class is created once then reused. `perOwnerShares` is the canonical owned
// count per owner, index-aligned to `value.selectedIds`.
//
// Full reconcile against an existing cap table: each owner is sent as a `create`,
// which the backend **upserts** by (stakeholder, owned corporation) — so a prefilled
// owner whose allocation changed updates in place (the ledger appends the delta and
// refreshes the % cache). Owners removed from the form are deleted up-front by
// `deleteRemovedOwners`. New owners are created.
async function reconcileOwners(args: {
  shareToken: string | undefined;
  value: OwnershipFieldValue;
  perOwnerShares: readonly number[];
  newStockClass: NewStockClassPayload;
  companyId: number;
  corporationId: number;
  // Per-owner governance role, index-aligned to selectedIds (membership only).
  governanceRoles?: readonly (string | undefined)[];
  // Per-owner GP flag, index-aligned to selectedIds (partnership only).
  isGeneralPartners?: readonly (boolean | undefined)[];
  // Reports a corporation minted for an entity owner, so the entry can record it.
  onEntityCreated?: (entryId: string, createdCorporationId: number) => void;
}): Promise<ReadonlyMap<string, number>> {
  const {
    value,
    perOwnerShares,
    newStockClass,
    companyId,
    corporationId,
    governanceRoles,
    isGeneralPartners,
    shareToken,
    onEntityCreated,
  } = args;
  const minted = new Map<string, number>();
  if (value.selectedIds.length === 0) return minted;
  const context = { companyId, corporationId };

  await value.selectedIds.reduce<Promise<void>>(async (prev, id, index) => {
    await prev;
    const entry = value.entries[id];
    if (!entry) return;
    const ownedShares = perOwnerShares[index] ?? 0;
    const governanceRole = governanceRoles?.[index];
    const isGeneralPartner = isGeneralPartners?.[index];
    const { owner } = entry;

    if (owner.kind === 'individual') {
      const identity =
        owner.directorId != null
          ? [{ path: 'directorId', value: owner.directorId }]
          : [
              {
                path: 'newIndividual',
                value: {
                  firstName: owner.firstName,
                  lastName: owner.lastName,
                  dateOfBirth: owner.dateOfBirth,
                  nationality: owner.nationality,
                },
              },
            ];
      await createCommendaOSEntity(
        'shareholding',
        [
          { path: 'type', value: 'INDIVIDUAL' },
          { path: 'ownedShares', value: ownedShares },
          ...identity,
          ...(governanceRole
            ? [{ path: 'governanceRole', value: governanceRole }]
            : []),
          ...(isGeneralPartner !== undefined
            ? [{ path: 'isGeneralPartner', value: isGeneralPartner }]
            : []),
          { path: 'newStockClass', value: newStockClass },
        ],
        context,
        shareToken
      );
      return;
    }

    // Entity owner: link an existing corporation, or create one then link
    // (corp creation has side effects, so it's its own write — create-then-link).
    let stakeholderCorporationId = owner.corporationId;
    if (stakeholderCorporationId == null) {
      const created = await createCommendaOSEntity(
        'corporation',
        [
          { path: 'legalName', value: owner.companyLegalName },
          { path: 'incorporationCountry', value: owner.incorporationCountry },
          ...(owner.jurisdiction
            ? [{ path: 'jurisdiction', value: owner.jurisdiction }]
            : []),
          // Required on create, but two thirds of the legal forms the content offers have no
          // legacy type to name them. Unknown is the stand-in the entity itself uses; the legal
          // form below is what actually says what this is.
          {
            path: 'businessEntityType',
            value: owner.entityType ?? CorporationType.Unknown,
          },
          ...(owner.entityLegalFormId
            ? [{ path: 'entityLegalFormId', value: owner.entityLegalFormId }]
            : []),
        ],
        context,
        shareToken
      );
      if (created.result.id == null) {
        throw new Error('Corporation create returned no id.');
      }
      stakeholderCorporationId = Number(created.result.id);
      // A later owner in this pass can still fail, and the notification asks the
      // user to submit again. Without recording the corporation just minted, that
      // retry mints a second one for the same entity, and the shareholding it then
      // asks for is a new stakeholder rather than the upsert the pass relies on —
      // so it lands on top of what was already written and the pool rejects it.
      // Recorded here as well as reported, so the value handed back to the form carries it even
      // when a later owner in this pass fails.
      minted.set(id, stakeholderCorporationId);
      onEntityCreated?.(id, stakeholderCorporationId);
    }
    await createCommendaOSEntity(
      'shareholding',
      [
        { path: 'type', value: 'INSTITUTION' },
        { path: 'ownedShares', value: ownedShares },
        { path: 'stakeholderCorporationId', value: stakeholderCorporationId },
        ...(governanceRole
          ? [{ path: 'governanceRole', value: governanceRole }]
          : []),
        ...(isGeneralPartner !== undefined
          ? [{ path: 'isGeneralPartner', value: isGeneralPartner }]
          : []),
        { path: 'newStockClass', value: newStockClass },
      ],
      context,
      shareToken
    );
  }, Promise.resolve()).catch((error: unknown) => {
    throw new OwnershipWriteError(error, withMintedIds(value, minted));
  });

  return minted;
}

// Delete owners that were prefilled from the cap table but removed in the form.
// Runs before the create/upsert pass so freed shares don't trip the ownership
// budget. The backend `deleteShareholding` also demotes the owner's BOR/HSC.
async function deleteRemovedOwners(
  removedStakeholdingIds: readonly string[] | undefined,
  companyId: number,
  corporationId: number,
  shareToken: string | undefined
): Promise<void> {
  if (!removedStakeholdingIds || removedStakeholdingIds.length === 0) return;
  const context = { companyId, corporationId };
  await removedStakeholdingIds.reduce<Promise<void>>(async (prev, id) => {
    await prev;
    await deleteCommendaOSEntity('shareholding', id, context, shareToken);
  }, Promise.resolve());
}

// Shares variant: the default "Common" class carries authorized + issued + par price.
export async function reconcileShareholdings(args: {
  shareToken: string | undefined;
  value: OwnershipFieldValue;
  computation: SharesComputation;
  companyId: number;
  corporationId: number;
  currency: string;
  // Prefilled stakeholdings the user removed in the form; deleted before the write pass.
  removedStakeholdingIds?: readonly string[];
  onEntityCreated?: (entryId: string, createdCorporationId: number) => void;
}): Promise<OwnershipFieldValue> {
  const {
    value,
    computation,
    companyId,
    corporationId,
    currency,
    removedStakeholdingIds,
    onEntityCreated,
  } = args;
  await deleteRemovedOwners(
    removedStakeholdingIds,
    companyId,
    corporationId,
    args.shareToken
  );
  if (value.selectedIds.length === 0) return value;
  // The class is minted with authorized shares (BE requires ≥ 1); fail clearly
  // here rather than letting the per-owner create 400 on validation.
  if (computation.authorizedShares < 1) {
    throw new Error(
      'Authorized shares must be set before owners can be saved.'
    );
  }
  const newStockClass: NewStockClassPayload = {
    name: 'Common',
    type: 'COMMON',
    authorizedShares: computation.authorizedShares,
    issuedShares: computation.issuedOutstanding,
    ...(computation.pricePerShare
      ? { pricePerShare: { amount: computation.pricePerShare, currency } }
      : {}),
  };
  const minted = await reconcileOwners({
    shareToken: args.shareToken,
    value,
    perOwnerShares: computation.perOwnerShares,
    newStockClass,
    companyId,
    corporationId,
    onEntityCreated,
  });
  return withMintedIds(value, minted);
}

// Membership variant: units back the class (in `authorizedShares`); price is 1,
// or capital ÷ units when the author issues units. `hasIssuedUnits` records the mode.
export async function reconcileMembership(args: {
  shareToken: string | undefined;
  value: OwnershipFieldValue;
  computation: MembershipComputation;
  companyId: number;
  corporationId: number;
  currency: string;
  removedStakeholdingIds?: readonly string[];
  onEntityCreated?: (entryId: string, createdCorporationId: number) => void;
}): Promise<OwnershipFieldValue> {
  const {
    value,
    computation,
    companyId,
    corporationId,
    currency,
    removedStakeholdingIds,
    onEntityCreated,
  } = args;
  await deleteRemovedOwners(
    removedStakeholdingIds,
    companyId,
    corporationId,
    args.shareToken
  );
  if (value.selectedIds.length === 0) return value;
  // Units (= authorized) must be ≥ 1 for the class to mint; fail clearly here.
  if (computation.totalUnits < 1) {
    throw new Error(
      'Total initial capital contribution must be set before members can be saved.'
    );
  }
  const newStockClass: NewStockClassPayload = {
    name: 'Common',
    type: 'COMMON',
    hasIssuedUnits: computation.hasIssuedUnits,
    managerManaged: computation.managerManaged,
    authorizedShares: computation.totalUnits,
    issuedShares: computation.unitsOutstanding,
    pricePerShare: { amount: computation.pricePerUnit, currency },
  };
  // M1: persist roles only when manager-managed; otherwise every member is a
  // Member (null role). A manager-managed member with no explicit pick → Member.
  const governanceRoles = computation.managerManaged
    ? value.selectedIds.map(
        (id) => value.entries[id]?.governanceRole ?? 'MEMBER'
      )
    : undefined;
  const minted = await reconcileOwners({
    shareToken: args.shareToken,
    value,
    perOwnerShares: computation.perOwnerUnits,
    newStockClass,
    companyId,
    corporationId,
    governanceRoles,
    onEntityCreated,
  });
  return withMintedIds(value, minted);
}

// Partnership variant: capital backs the class at price 1 (contribution = ownedShares);
// each partner carries `isGeneralPartner` (exactly one GP, enforced in the node).
export async function reconcilePartnership(args: {
  shareToken: string | undefined;
  value: OwnershipFieldValue;
  computation: PartnershipComputation;
  companyId: number;
  corporationId: number;
  currency: string;
  removedStakeholdingIds?: readonly string[];
  onEntityCreated?: (entryId: string, createdCorporationId: number) => void;
}): Promise<OwnershipFieldValue> {
  const {
    value,
    computation,
    companyId,
    corporationId,
    currency,
    removedStakeholdingIds,
    onEntityCreated,
  } = args;
  await deleteRemovedOwners(
    removedStakeholdingIds,
    companyId,
    corporationId,
    args.shareToken
  );
  if (value.selectedIds.length === 0) return value;
  // Capital (= authorized, price 1) must be ≥ 1 for the class to mint.
  if (computation.totalShareCapital < 1) {
    throw new Error(
      'Total capital contribution must be set before partners can be saved.'
    );
  }
  const newStockClass: NewStockClassPayload = {
    name: 'Common',
    type: 'COMMON',
    authorizedShares: computation.totalShareCapital,
    issuedShares: computation.totalCapitalAllocated,
    pricePerShare: { amount: 1, currency },
  };
  const isGeneralPartners = value.selectedIds.map(
    (id) => !!value.entries[id]?.isGeneralPartner
  );
  const minted = await reconcileOwners({
    shareToken: args.shareToken,
    value,
    perOwnerShares: computation.perOwnerCapital,
    newStockClass,
    companyId,
    corporationId,
    isGeneralPartners,
    onEntityCreated,
  });
  return withMintedIds(value, minted);
}
